/**
 * NotificationBell.tsx
 *
 * Header bell surfacing pending human-in-the-loop approval gates across all
 * runs, plus browser notifications when a new gate appears.
 *
 * Data (polled every 30s):
 *   • GET /v1/approvals?status=pending  — pending gates (all runs)
 *   • GET /v1/runs?status=running,queued,waiting,admitted — best-effort
 *     runId → agentName map (approvals carry no agent name)
 *
 * Browser notifications fire once per gate ID (tracked in a ref), only when
 * permission is granted; permission is requested on mount and denial is a no-op.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell, ChevronRight, Clock } from 'lucide-react'

import { api } from '../../api/client'

const POLL_MS = 30_000

interface ApprovalRow {
  id: string
  runId: string
  stepId?: string
  skillId?: string
  requestedAt: string
}

interface RunRow {
  id: string
  agentId?: string
  agentName?: string
}

function shortId(id?: string): string {
  if (!id) return '—'
  return id.length > 10 ? `${id.slice(0, 8)}…` : id
}

function fmtAgo(iso?: string): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (isNaN(ms)) return '—'
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${m % 60}m`
  return `${Math.floor(h / 24)}d`
}

function extractRuns(json: unknown): RunRow[] {
  if (Array.isArray(json)) return json as RunRow[]
  const o = json as { items?: RunRow[]; runs?: RunRow[] } | null
  return (o?.items ?? o?.runs ?? []) as RunRow[]
}

export interface NotificationBellProps {
  tenantId: string
  onReviewRun: (runId: string) => void
}

export function NotificationBell({ tenantId, onReviewRun }: NotificationBellProps) {
  const [approvals, setApprovals] = useState<ApprovalRow[]>([])
  const [open, setOpen] = useState(false)

  // Gate IDs we've already fired a browser notification for (fire once each).
  const notifiedIds = useRef<Set<string>>(new Set())
  // Best-effort runId → agentName, accumulated across polls.
  const agentByRun = useRef<Map<string, string>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)

  const agentLabel = useCallback((a: ApprovalRow) => {
    return agentByRun.current.get(a.runId) ?? shortId(a.runId)
  }, [])

  // ── Browser notification permission (request once on mount) ─────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* denial / unsupported → no-op */ })
    }
  }, [])

  // ── Poll ────────────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    const [approvalsRes, runsRes] = await Promise.allSettled([
      api.get<{ items?: ApprovalRow[] }>('/v1/approvals', { params: { status: 'pending', limit: 50 } }),
      api.get<unknown>('/v1/runs', { params: { status: 'running,queued,waiting,admitted', pageSize: 50 } }),
    ])

    if (runsRes.status === 'fulfilled') {
      for (const r of extractRuns(runsRes.value)) {
        if (r.agentName) agentByRun.current.set(r.id, r.agentName)
      }
    }

    if (approvalsRes.status !== 'fulfilled') return
    const items = approvalsRes.value.items ?? []
    setApprovals(items)

    // Fire a browser notification for each newly-seen gate ID.
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      for (const a of items) {
        if (notifiedIds.current.has(a.id)) continue
        notifiedIds.current.add(a.id)
        const name = agentByRun.current.get(a.runId) ?? 'An agent'
        try {
          new Notification('Approval needed', { body: `Agent ${name} is waiting for your review`, tag: a.id })
        } catch { /* ignore */ }
      }
    } else {
      // Still record IDs so enabling permission later doesn't backfire a flood.
      for (const a of items) notifiedIds.current.add(a.id)
    }
  }, [])

  useEffect(() => {
    void refresh()
    const t = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(t)
  }, [refresh, tenantId])

  // ── Outside-click closes the dropdown ────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const count = approvals.length

  return (
    <div className="relative flex-shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={count > 0 ? `${count} pending approvals` : 'Notifications'}
        aria-expanded={open}
        title={count > 0 ? `${count} pending approval${count === 1 ? '' : 's'}` : 'No pending approvals'}
        className="relative w-[30px] h-[30px] rounded-full border border-[rgba(148,163,184,0.18)] bg-[rgba(148,163,184,0.06)] text-[#8fa3bd] flex items-center justify-center cursor-pointer transition-[box-shadow,border-color,color] duration-150 hover:text-[#e2ecf9] hover:border-[rgba(6,182,212,0.4)] hover:shadow-[0_0_8px_rgba(6,182,212,0.25)]"
      >
        <Bell size={15} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-[4px] rounded-full bg-[#ef4444] text-white text-[10px] font-[800] leading-[16px] text-center"
            aria-hidden="true"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[38px] w-[340px] max-h-[440px] overflow-y-auto sds-body-scroll rounded-[12px] border border-[rgba(148,163,184,0.14)] bg-[rgba(10,16,24,0.97)] backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.5)] z-[200]">
          <div className="px-4 py-3 border-b border-[rgba(148,163,184,0.1)] flex items-center justify-between">
            <span className="text-[12px] font-[800] text-[#dce9f7] tracking-[-0.01em]">Pending approvals</span>
            {count > 0 && <span className="text-[10.5px] font-bold text-[#fbbf24]">{count}</span>}
          </div>

          {count === 0 ? (
            <div className="px-4 py-5 text-[12px] text-[#4e6480]">No pending approvals.</div>
          ) : (
            <div className="flex flex-col">
              {approvals.map((a) => (
                <div key={a.id} className="px-4 py-3 border-b border-[rgba(148,163,184,0.06)] last:border-b-0 flex flex-col gap-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-bold text-[#dce9f7] truncate">{agentLabel(a)}</div>
                      <div className="text-[10px] text-[#4e6480] font-mono truncate">run {shortId(a.runId)}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#fbbf24] whitespace-nowrap flex-shrink-0">
                      <Clock size={10} /> {fmtAgo(a.requestedAt)}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#8fa3bd]">
                    Waiting on <span className="text-[#c8d9ee] font-semibold">{a.skillId || a.stepId || 'approval'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { onReviewRun(a.runId); setOpen(false) }}
                    className="self-start inline-flex items-center gap-[5px] px-[10px] py-[5px] rounded-[7px] border border-[rgba(245,158,11,0.45)] bg-[rgba(245,158,11,0.14)] text-[#fbbf24] text-[10.5px] font-[800] cursor-pointer transition-colors hover:bg-[rgba(245,158,11,0.22)]"
                  >
                    Review <ChevronRight size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
